<script setup>

import InnerProofreadingSection from "@/views/preview/sections/InnerProofreadingSection.vue";
import { useActiveDeviceRenderStore } from "@/stores/activeDeviceRender";
import PreviewScanError from "@/views/preview/PreviewScanError.vue";
import { onMounted, onUnmounted, ref } from "vue";

// Helper variables
let popoverTriggerList = [];
let popoverList = [];

// Init popovers on content loaded
onMounted(() => {
  // Grab all popover containers..
  popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
  );

  // ..and init them
  popoverList = popoverTriggerList.map(function (popoverTriggerEl) {
    return new window.bootstrap.Popover(popoverTriggerEl, {
      container: popoverTriggerEl.dataset.bsContainer || "#page-container",
      animation:
          popoverTriggerEl.dataset.bsAnimation &&
          popoverTriggerEl.dataset.bsAnimation.toLowerCase() == "true"
              ? true
              : false,
      trigger: popoverTriggerEl.dataset.bsTrigger || "hover focus",
    });
  });
});

// Dispose popovers on unMounted
onUnmounted(() => {
  popoverList.forEach((popover) => popover.dispose());
});

const activeDeviceRender = useActiveDeviceRenderStore();
const processedData = ref([]);
const props = defineProps({
  devicePreview: Object,
  feedbackCategory: {
    type: String,
    default: "proofread",
  },
});

const integrityCategories = [
  'ai_readiness.crawlability',
  'ai_readiness.visual_state_indicators',
  'ai_readiness.semantic_hierarchy',
  'ai_readiness.ghost_content',
  'ai_readiness.text_in_image',
  'ai_readiness.dynamic_content',
]

function botsAllowed(bots) {
  for (const bot of bots) {
    if (activeDeviceRender.page.crawlRules[bot] && activeDeviceRender.page.crawlRules[bot].crawl.allowed === false) {
      return false;
    }
  }
  return true;
}

function processedDataCategory(categories, level='') {
  let issues = processedData.value.filter(item => categories.includes(item.category))
  if (!level){
    return issues.length;
  }
  return issues.filter(v => v.severity === level).length;
}

</script>

<template>
  <div class="row p-2 mt-3" v-if="false">

    <!-- Layout Checks -->
    <div class="col-md-12 audit-summary">

      <div class="audit-evidence audit-evidence-custom h-100 pb-2" style="--evidence-label: 'AI READINESS CHECKS'">
        <div class="row ms-1">
          <div class="col-12">
            <div class="d-flex align-items-center justify-content-between py-1 border-bottom">
              <div class="d-flex align-items-center gap-2">
                <i class="fa fa-robot"></i>
                <div class="fw-semibold me-2">Crawlability:</div>
              </div>

              <div class="d-flex align-items-center">
                <div class="bot-crawl-logo">
                  <img
                      data-bs-toggle="popover"
                      data-bs-html="true"
                      data-bs-placement="top"
                      data-bs-content="<b>ChatGPT</b><br>gptbot<br>chatgpt-user"

                      src="/assets/images/openai-brands-solid-full.svg" width="22">
                  <div
                      :class="botsAllowed(['gptbot', 'chatgpt-user']) ? 'allowed-bot' : 'denied-bot'"
                      class="bot-status-indicator"></div>
                </div>
                <div class="bot-crawl-logo">
                  <img v-tooltip="`Grok`"
                       src="/assets/images/grok.svg" width="24">
                  <div
                      :class="botsAllowed(['grokbot']) ? 'allowed-bot' : 'denied-bot'"
                      class="bot-status-indicator"></div>
                </div>
                <div class="bot-crawl-logo">
                  <img v-tooltip="`Perplexity`"
                       src="/assets/images/perplexity.svg" width="21">
                  <div
                      :class="botsAllowed(['perplexitybot', 'perplexity-user']) ? 'allowed-bot' : 'denied-bot'"
                      class="bot-status-indicator"></div>
                </div>
                <div class="bot-crawl-logo">
                  <img v-tooltip="`Google Gemini`"
                       src="/assets/images/gemini-color.svg" width="22">
                  <div
                      :class="botsAllowed(['gemini-deep-research', 'google-extended']) ? 'allowed-bot' : 'denied-bot'"
                      class="bot-status-indicator"></div>
                </div>
                <div class="bot-crawl-logo">
                  <img v-tooltip="`Google Search`"
                       src="/assets/images/google.svg" width="22">
                  <div
                      :class="botsAllowed(['googlebot', 'google-extended']) ? 'allowed-bot' : 'denied-bot'"
                      class="bot-status-indicator"></div>
                </div>
                <div class="bot-crawl-logo">
                  <img v-tooltip="`Bing`"
                       src="/assets/images/bing.svg" width="22">
                  <div
                      :class="botsAllowed(['bingbot']) ? 'allowed-bot' : 'denied-bot'"
                      class="bot-status-indicator"></div>
                </div>
                <div class="bot-crawl-logo">
                  <img v-tooltip="`Claude`"
                       src="/assets/images/claude.svg" width="22">
                  <div
                      :class="botsAllowed(['claudebot', 'claude-searchbot', 'claude-user']) ? 'allowed-bot' : 'denied-bot'"
                      class="bot-status-indicator"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="row mt-2 ms-1">
          <div class="col-6 border-end">
            <div class="d-flex align-items-center justify-content-between py-1 border-bottom">
              <div class="d-flex align-items-center gap-2">
                <i class="fa fa-eye"></i><span>Visual Clarity</span></div>
              <span v-if="processedDataCategory(['visual_state_indicators']) === 0" class="small status-pass"><i class="fa-solid fa-circle-check"></i> Pass</span>
              <span v-else-if="processedDataCategory(['visual_state_indicators'], 'high') > 0" class="small status-danger"><i class="fa-solid fa-circle-xmark"></i> {{processedDataCategory(['visual_state_indicators'], 'high')}}</span>
              <span v-else-if="processedDataCategory(['visual_state_indicators'], 'medium') > 0" class="small status-warn"><i class="fa-solid fa-circle-xmark"></i> {{processedDataCategory(['visual_state_indicators'], 'medium')}}</span>
              <span v-else-if="processedDataCategory(['visual_state_indicators'], 'low') > 0" class="small status-suggestion"><i class="fa-solid fa-circle-xmark"></i> {{processedDataCategory(['visual_state_indicators'], 'low')}}</span>
            </div>
          </div>
          <div class="col-6">
            <div class="d-flex align-items-center justify-content-between py-1 border-bottom">
              <div class="d-flex align-items-center gap-2">

                <i class="fa fa-code"></i><span>Page Semantics</span></div>

              <span v-if="processedDataCategory(['semantic_hierarchy']) === 0" class="small status-pass"><i class="fa-solid fa-circle-check"></i> Pass</span>
              <span v-else-if="processedDataCategory(['semantic_hierarchy'], 'high') > 0" class="small status-danger"><i class="fa-solid fa-circle-xmark"></i> {{processedDataCategory(['semantic_hierarchy'], 'high')}}</span>
              <span v-else-if="processedDataCategory(['semantic_hierarchy'], 'medium') > 0" class="small status-warn"><i class="fa-solid fa-circle-xmark"></i> {{processedDataCategory(['semantic_hierarchy'], 'medium')}}</span>
              <span v-else-if="processedDataCategory(['semantic_hierarchy'], 'low') > 0" class="small status-suggestion"><i class="fa-solid fa-circle-xmark"></i> {{processedDataCategory(['semantic_hierarchy'], 'low')}}</span>
            </div>
          </div>
          <div class="col-6 border-end">
            <div class="d-flex align-items-center justify-content-between py-1 border-bottom">
              <div class="d-flex align-items-center gap-2">
                <i class="fa fa-ghost"></i><span>Hidden Content</span></div>
              <span v-if="processedDataCategory(['ghost_content', 'text_in_image']) === 0" class="small status-pass"><i class="fa-solid fa-circle-check"></i> Pass</span>
              <span v-else-if="processedDataCategory(['ghost_content', 'text_in_image'], 'high') > 0" class="small status-danger"><i class="fa-solid fa-circle-xmark"></i> {{processedDataCategory(['ghost_content', 'text_in_image'], 'high')}}</span>
              <span v-else-if="processedDataCategory(['ghost_content', 'text_in_image'], 'medium') > 0" class="small status-warn"><i class="fa-solid fa-circle-xmark"></i> {{processedDataCategory(['ghost_content', 'text_in_image'], 'medium')}}</span>
              <span v-else-if="processedDataCategory(['ghost_content', 'text_in_image'], 'low') > 0" class="small status-suggestion"><i class="fa-solid fa-circle-xmark"></i> {{processedDataCategory(['ghost_content', 'text_in_image'], 'low')}}</span>
            </div>
          </div>
          <div class="col-6">
            <div class="d-flex align-items-center justify-content-between py-1 border-bottom">
              <div class="d-flex align-items-center gap-2">
                <i class="fa fa-bolt-lightning"></i><span>Dynamic Content</span></div>
              <span v-if="processedDataCategory(['dynamic_content']) === 0" class="small status-pass"><i class="fa-solid fa-circle-check"></i> Pass</span>
              <span v-else-if="processedDataCategory(['dynamic_content'], 'high') > 0" class="small status-danger"><i class="fa-solid fa-circle-xmark"></i> {{processedDataCategory(['dynamic_content'], 'high')}}</span>
              <span v-else-if="processedDataCategory(['dynamic_content'], 'medium') > 0" class="small status-warn"><i class="fa-solid fa-circle-xmark"></i> {{processedDataCategory(['dynamic_content'], 'medium')}}</span>
              <span v-else-if="processedDataCategory(['dynamic_content'], 'low') > 0" class="small status-suggestion"><i class="fa-solid fa-circle-xmark"></i> {{processedDataCategory(['dynamic_content'], 'low')}}</span>
            </div>
          </div>
        </div>


      </div>
    </div>

  </div>
  <preview-scan-error v-if="activeDeviceRender.page.proofreadingState === 'error'"/>

  <div v-else id="ai-insights-accordion" class="audit-summary">
    <inner-proofreading-section :devicePreview="devicePreview" :title="'Content Integrity '"
                                :icon="`fa fa-magnifying-glass-chart`"
                                @update:processedData="processedData = $event"
                                :categories=integrityCategories></inner-proofreading-section>
  </div>

</template>

<style>
/* Styles consolidated to src/assets/scss/custom/_preview-sections.scss */
</style>